import { Component } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap/modal/modal.module';
import { ToastsManager } from 'ng2-toastr/ng2-toastr';
import { Router } from '@angular/router';
import { ScrollToService, ScrollToConfigOptions } from '@nicky-lenaers/ngx-scroll-to';
import {
  GooglePlacesService,
  CampusAddressService,
  MessagesService,
  ConversationsService,
  RestaurantService
} from '../../providers';
import forEach from 'lodash-es/forEach';
import includes from 'lodash-es/includes';
import extend from 'lodash-es/extend';

@Component({
  selector: 'messenger_modal',
  styleUrls: ['./messenger_modal.scss'],
  templateUrl: './messenger_modal.pug'
})

export class MessengerModalComponent {
  public search: string;
  public conversation = JSON.parse(window.localStorage.getItem('assistantChat'));
  public messageForm: FormGroup;
  public messages: Object[] = [];
  public submitted: boolean;
  public responceType: string = 'message';
  public propositions: Object[] = [];
  public variants: Object[] = [];
  public suggestions: Array<string>;
  public campusPredictions: Array<string>
  public addressPredictions: Array<string>
  public suggestedHalls: Array<Object>;
  public typing: boolean = false;
  public requestAction: string;

  constructor(public activeModal: NgbActiveModal,
              private googlePlacesService: GooglePlacesService,
              private campusService: CampusAddressService,
              private toastr: ToastsManager,
              private scrollToService: ScrollToService,
              private messageService: MessagesService,
              private conversationService: ConversationsService,
              private restaurantService: RestaurantService,
              private router: Router,
              private _fb: FormBuilder) {}

  ngOnInit() {
    this.messageForm = this._fb.group(this.formInit());

    if (this.conversation) {
      this.messages = this.conversation.messages;
      window.localStorage.setItem('conversationId', this.conversation['id']);

      setTimeout(() => {
        this.scrollToBottom();
      }, 200);
    }

    this.conversationService.messageAdded.subscribe(
      (message: Object) => {
        this.messages.push(message);
        this.scrollToBottom();
      }
    );

    this.conversationService.messagesCleared.subscribe(
      () => {
        this.messages = [];
      }
    );

    this.conversationService.conversationSet.subscribe(
      (conversation: Object) => {
        this.conversation = conversation;
        window.localStorage.setItem('conversationId', this.conversation['id']);
      }
    );

    this.loadInitialVariants();
    this.requestAddress();
  }

  loadInitialVariants() {
    let newConversation = true;
    if (newConversation) {
      this.conversationService.start().subscribe((data) => {
        this.messageForm.reset();
        this.handleResponse(data.bot_response);
      })
    }
  }

  formInit() {
    const message = {
      body: ['', Validators.compose([Validators.required])]
    };
    return message;
  }

  submitForm() {
    this.submitted = true;
    if (this.messageForm.valid) {
      this.sendTextMessage();
    }
  }

  sendMessage(requestData) {
    this.conversationService.addMessage({ body: requestData.body, own: true });

    let address = window.localStorage.getItem('currentUserAddress');
    let conversationId = window.localStorage.getItem('conversationId');
    let params = {
      message: requestData,
      location: address,
      guest_token: this.conversation['guest_token']
    }

    extend(requestData, { conversation_id: conversationId })
    this.messageForm.reset();
    this.submitted = false;
    this.scrollToBottom();

    this.messageService.create(params).subscribe((data) => {
      this.handleResponse(data.bot_response);
    }, (err) => {
      this.toastr.info('Sorry, but something went wrong.');
    })
  }

  sendTextMessage() {
    let params = this.messageForm.value;
    let requestData;
    if (this.requestAction) {
      requestData = {
        request: {
          action: this.requestAction
        }
      };
      extend(params, requestData)
    }
    this.sendMessage(params);
  }

  selectProposition(proposition) {
    if (proposition.request.action && proposition.body != 'Type another') {
      this.sendMessage(proposition);
    } else {
      this.responceType = 'message';
    }
  }

  selectVariant(variant) {
    this.conversationService.addMessage({ body: variant.variant_label, own: true });
    this.handleResponse(variant);
  }

  clearAssistant() {
    this.conversationService.clearMessages();
  }

  handleResponse(data) {
    this.messageForm.reset();
    this.conversationService.addMessage({ body: data.response_message, own: false });
    this.scrollToBottom();
    this.submitted = false;

    //TODO: Use Factory or Builder pattern here to avoid conditions
    if (data.form_type) {
      this.responceType = data.form_type;
    } else {
      this.responceType = 'message';
    }

    if (data.propositions && data.propositions.length > 0) {
      this.propositions = data.propositions;
      let typeAnotherOption = this.propositions.slice(-1)[0];
      if (typeAnotherOption['request'] && typeAnotherOption['request']['action']) {
        this.requestAction = typeAnotherOption['request']['action'];
      }
    }

    if (data.variants && data.variants.length > 0) {
      this.variants = data.variants;
    }

    if (data.restaurants) {
      this.restaurantService.suggestedByBot = data.restaurants;
      this.router.navigate(['./results']);
    }
  }

  wouldLikeAnother() {
    window.localStorage.removeItem('currentUserAddress');
    this.loadInitialVariants();
  }

  endConversation() {
    let params = {};
    let guestToken = this.conversation['guest_token'];

    if (guestToken && guestToken.length > 0) {
      params = { guest_token: guestToken };
    }
    this.conversationService.update(this.conversation['id'], params).subscribe((data) => {
      this.clearAssistant();
      this.activeModal.dismiss();
    })
  }

  typeAnotherSelected() {
    this.responceType = 'message';
  }

  scrollToBottom() {
    const config: ScrollToConfigOptions = {
      target: 'chat-bottom'
    };

    this.scrollToService.scrollTo(config);
  }

  addressPresent() {
    let address = window.localStorage.getItem('currentUserAddress');
    return (address && address.length > 0);
  }

  addAddress() {
    window.localStorage.setItem('currentUserAddress', this.search);
    this.search = '';
  }

  requestAddress() {
    let address = window.localStorage.getItem('currentUserAddress');
    if (address && address.length == 0) {
      // TODO set Address from user
      // window.localStorage.getItem('currentAddress')
    }
  }

  campusSuggested(suggestion: string) {
    return includes(this.campusPredictions, suggestion);
  }

  searchAutocomplete(query: string) {
    this.googlePlacesService.getPredictions({ input: query });
    this.showCampusPredictions(query);
    setTimeout(() => {
      this.showAddressPredictions();
      this.suggestions = this.campusPredictions.concat(this.addressPredictions);
    }, 200);
  }

  showAddressPredictions() {
    let predictions: Array<string> = [];
    forEach(this.googlePlacesService.predictions, (prediction: Object) => {
      predictions.push(prediction['description'])
    })
    this.addressPredictions = predictions;
  }

  showCampusPredictions(query: string) {
    let predictions: Array<string> = []
    this.campusService.autocompleteHalls(query).subscribe((data) => {
      this.suggestedHalls = data['addresses'];
      forEach(this.suggestedHalls, (hall: Object) => {
          let prediction = hall['address_name'] + ', ' + hall['campus_name'];
          predictions.push(prediction)
        })
    });
    this.campusPredictions = predictions;
  }
}



// WEBPACK FOOTER //
// ./src/components/messenger_modal/messenger_modal.ts